import { getCurrencyLocale } from "./currencyLocale";

export const DEFAULT_CURRENCY = "USD" as const;
export type CurrencyCode = string;

const validCurrencyCodes = new Set<string>();
const invalidCurrencyCodes = new Set<string>();

const getValidCurrencyCode = (currency?: CurrencyCode | undefined): string => {
    if (!currency) {
        return DEFAULT_CURRENCY;
    }

    const normalizedCurrency = currency.toUpperCase();

    if (validCurrencyCodes.has(normalizedCurrency)) {
        return normalizedCurrency;
    }

    if (invalidCurrencyCodes.has(normalizedCurrency)) {
        return DEFAULT_CURRENCY;
    }

    try {
        new Intl.NumberFormat("en", { style: "currency", currency: normalizedCurrency });
        validCurrencyCodes.add(normalizedCurrency);
        return normalizedCurrency;
    } catch {
        invalidCurrencyCodes.add(normalizedCurrency);

        if (import.meta.env.DEV) {
            console.warn(
                `[formatCurrency] Unknown currency code "${currency}", falling back to ${DEFAULT_CURRENCY}.`
            );
        }

        return DEFAULT_CURRENCY;
    }
};

const getDecimalPlaces = (num: number): number => {
    const str = String(num);
    const decimalIndex = str.indexOf(".");
    if (decimalIndex === -1) return 0;
    return str.length - decimalIndex - 1;
};

export const formatCurrency = (amount: number, currency?: CurrencyCode | undefined) => {
    const validCurrency = getValidCurrencyCode(currency);
    const locale = getCurrencyLocale();
    const fractionDigits = getDecimalPlaces(amount);
    const options: Intl.NumberFormatOptions = {
        style: "currency",
        currency: validCurrency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    };
    return new Intl.NumberFormat(locale, options).format(amount);
};

export const formatCurrencyLabel = (currency?: CurrencyCode | undefined) => {
    if (!currency) return "";

    const validCurrency = getValidCurrencyCode(currency);
    const options: Intl.NumberFormatOptions = { style: "currency", currency: validCurrency };
    const parts = new Intl.NumberFormat(getCurrencyLocale(), options).formatToParts();

    return parts.find((part) => part.type === "currency")?.value || validCurrency;
};
